/**
 * Wave C drawable location fields — parse BE meta / confluence evidence only.
 * Never invent geometry from chart OHLC.
 */

export type StrategyRdWaveCDirection = "long" | "short" | "neutral" | null;

export type StrategyRdWaveCPivot = {
  barTime: string;
  price: number;
  barIndex: number | null;
  kind: string | null;
};

/** Trendline endpoint from BE `upper_line` / `lower_line` / `pole` (wedge / flag). */
export type StrategyRdWaveCLineEnd = {
  barTime: string;
  price: number;
  barIndex: number | null;
  kind: string | null;
};

export type StrategyRdWaveCTrendLine = {
  p1: StrategyRdWaveCLineEnd;
  p2: StrategyRdWaveCLineEnd;
};

/** One sample on BE `donchian_path[]`. */
export type StrategyRdDonchianPathPoint = {
  barTime: string;
  barIndex: number | null;
  high: number | null;
  low: number | null;
  mid: number | null;
};

export type StrategyRdWaveCLocation = {
  barTime: string | null;
  barIndex: number | null;
  direction: StrategyRdWaveCDirection;
  candleType: string | null;
  patternStart: number | null;
  patternEnd: number | null;
  patternStartTime: string | null;
  patternEndTime: string | null;
  patternHigh: number | null;
  patternLow: number | null;
  levelHigh: number | null;
  levelLow: number | null;
  neckline: number | null;
  /** Sloped neckline anchors (BE H&S). */
  necklineSlope: number | null;
  necklineLeft: number | null;
  necklineRight: number | null;
  necklineLeftBar: number | null;
  necklineRightBar: number | null;
  /** Named H&S anchors (BE). Prefer these over unlabeled pivots for labels. */
  leftShoulder: number | null;
  head: number | null;
  rightShoulder: number | null;
  leftShoulderTime: string | null;
  headTime: string | null;
  rightShoulderTime: string | null;
  pivots: StrategyRdWaveCPivot[];
  /** Wedge / flag channel trendlines (prefer over bound+slope). */
  upperLine: StrategyRdWaveCTrendLine | null;
  lowerLine: StrategyRdWaveCTrendLine | null;
  /** Flag / pennant mast: BE `pole.p1` → `pole.p2`. */
  pole: StrategyRdWaveCTrendLine | null;
  /** BE `channel_kind`: flag / pennant / triangle / rectangle. */
  channelKind: "flag" | "pennant" | "triangle" | "rectangle" | null;
  /** BE `triangle_kind`: ascending / descending / symmetrical. */
  triangleKind: "asc" | "desc" | "sym" | null;
  /** Pennant: converging triangle body (`converging: true`). */
  converging: boolean | null;
  widthStart: number | null;
  widthEnd: number | null;
  breakLevel: number | null;
  upperBound: number | null;
  lowerBound: number | null;
  upperSlope: number | null;
  lowerSlope: number | null;
  spanStart: number | null;
  spanEnd: number | null;
  spanStartTime: string | null;
  spanEndTime: string | null;
  /** Donchian channel window (BE `channel_start_time` → `channel_end_time`). */
  channelStartTime: string | null;
  channelEndTime: string | null;
  /** Stepped Donchian envelope through the signal (`donchian_path[]`). */
  donchianPath: StrategyRdDonchianPathPoint[];
  /** BE `chart_draw.kind` when present (e.g. `donchian_channel`). */
  chartDrawKind: string | null;
  /**
   * BE `chart_draw.osc_series` — which FE-owned series `osc_line` Y values sit on.
   * `histogram` | `macd_line` | `rsi` | `stoch_k`
   */
  chartDrawOscSeries: string | null;
  /** BE `chart_draw.signal_bar_pills` (e.g. `["enter","div"]`). */
  chartDrawSignalBarPills: string[];
  /** Echoed MACD / osc settings from match meta (FE series must match). */
  oscSource: string | null;
  oscFast: number | null;
  oscSlow: number | null;
  oscSignal: number | null;
  oscPeriod: number | null;
  oscLength: number | null;
  oscSmoothK: number | null;
  oscSmoothD: number | null;
  upper: number | null;
  mid: number | null;
  lower: number | null;
  zone: string | null;
  positionRatio: number | null;
  donchianHigh: number | null;
  donchianLow: number | null;
  maValue: number | null;
  cupLeft: number | null;
  cupRight: number | null;
  handleBar: number | null;
  cupLeftTime: string | null;
  cupRightTime: string | null;
  cupBottomTime: string | null;
  handleBarTime: string | null;
  handlePrice: number | null;
  rim: number | null;
  cupLow: number | null;
  cupHigh: number | null;
  /** Ordered samples along the cup U (BE). Prefer over corner diagonals. */
  cupPath: StrategyRdWaveCPivot[];
  poleStart: number | null;
  poleEnd: number | null;
  flagStart: number | null;
  flagEnd: number | null;
  poleStartTime: string | null;
  poleEndTime: string | null;
  flagStartTime: string | null;
  flagEndTime: string | null;
  sessionId: string | null;
  hour: number | null;
  rsi: number | null;
  macd: number | null;
  macdSignal: number | null;
  macdHist: number | null;
  /** MACD "Momentum improving": the histogram on the bar before the setup. */
  macdHistPrev: number | null;
  stochK: number | null;
  stochD: number | null;
  mfi: number | null;
  adx: number | null;
  atrPct: number | null;
  /** ATR regime: percentile of ATR % in the instrument's last `rank_lookback` bars (0–100). */
  atrRank: number | null;
  bucket: string | null;
  rs: number | null;
  mansfieldRs: number | null;
  corr: number | null;
  paceMultiple: number | null;
  upDownVolRatio: number | null;
  /** Bar volume (BE match meta / confluence evidence when series has volume). */
  volume: number | null;
  volumePrior: number | null;
  volExpandRatio: number | null;
  volExpandFactor: number | null;
  volExpanded: boolean | null;
  rvol: number | null;
  rvolAvg: number | null;
  rvolLookback: number | null;
  volumeAvailable: boolean | null;
  softPass: string | null;
  /** Divergence swings (legacy pivot indexes + Alchemy `price_line` / `osc_line`). */
  divPivot1Time: string | null;
  divPivot2Time: string | null;
  divPivot1Index: number | null;
  divPivot2Index: number | null;
  divPrice1: number | null;
  divPrice2: number | null;
  divOsc1: number | null;
  divOsc2: number | null;
  divClass: string | null;
  /** `bull` | `bear` (BE `side`) — maps to long/short drawing color. */
  divSide: string | null;
  /** Alchemy-style swing rays (`price_line` / `osc_line` with p1/p2). */
  divPriceLine: StrategyRdWaveCTrendLine | null;
  divOscLine: StrategyRdWaveCTrendLine | null;
  /** Dash at 2nd price pivot (Alchemy reclaim). */
  divReclaimLevel: number | null;
  /** Dash at intervening opposite swing (bull → pivot high). */
  divStructureBreakLevel: number | null;
  /** When close crosses the structure level (optional highlight). */
  divStructureBreakBarTime: string | null;
  divStructureBreakBarIndex: number | null;
  /** Intervening opposite-swing pivot that defines `structure_break_level`. */
  divStructureBreakPivotTime: string | null;
  divStructureBreakPivotIndex: number | null;
  /** e.g. `Classic Bull Div`. */
  divLabel: string | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function finiteNum(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function asTime(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const t = value.trim();
  return t || null;
}

function asDirection(value: unknown): StrategyRdWaveCDirection {
  if (typeof value !== "string") return null;
  const d = value.trim().toLowerCase();
  if (d === "long" || d === "short" || d === "neutral") return d;
  if (d === "bull" || d === "bullish") return "long";
  if (d === "bear" || d === "bearish") return "short";
  return null;
}

function parseLineEnd(raw: unknown): StrategyRdWaveCLineEnd | null {
  const row = asRecord(raw);
  if (!row) return null;
  const barTime = asTime(row.bar_time);
  const price = finiteNum(row.price);
  if (!barTime || price == null) return null;
  const barIndex = finiteNum(row.bar_index);
  const kind = typeof row.kind === "string" ? row.kind.trim() || null : null;
  return {
    barTime,
    price,
    barIndex: barIndex != null ? Math.round(barIndex) : null,
    kind,
  };
}

function parseTrendLine(raw: unknown): StrategyRdWaveCTrendLine | null {
  const row = asRecord(raw);
  if (!row) return null;
  const p1 = parseLineEnd(row.p1);
  const p2 = parseLineEnd(row.p2);
  if (!p1 || !p2) return null;
  if (p1.barTime === p2.barTime && p1.price === p2.price) return null;
  return { p1, p2 };
}

/** Interpolate / extrapolate price along a trendline at a target bar index. */
export function strategyRdTrendLinePriceAt(
  line: StrategyRdWaveCTrendLine,
  atBarIndex: number | null
): number | null {
  if (atBarIndex == null) return null;
  const i0 = line.p1.barIndex;
  const i1 = line.p2.barIndex;
  if (i0 == null || i1 == null || i1 === i0) return line.p2.price;
  const t = (atBarIndex - i0) / (i1 - i0);
  return line.p1.price + t * (line.p2.price - line.p1.price);
}

function parsePivots(raw: unknown): StrategyRdWaveCPivot[] {
  if (!Array.isArray(raw)) return [];
  const out: StrategyRdWaveCPivot[] = [];
  for (const p of raw) {
    const row = asRecord(p);
    if (!row) continue;
    const barTime = asTime(row.bar_time);
    const price = finiteNum(row.price);
    if (!barTime || price == null) continue;
    const barIndex = finiteNum(row.bar_index);
    const kind = typeof row.kind === "string" ? row.kind.trim() || null : null;
    out.push({
      barTime,
      price,
      barIndex: barIndex != null ? Math.round(barIndex) : null,
      kind,
    });
  }
  return out;
}

function parseDonchianPath(raw: unknown): StrategyRdDonchianPathPoint[] {
  if (!Array.isArray(raw)) return [];
  const out: StrategyRdDonchianPathPoint[] = [];
  for (const p of raw) {
    const row = asRecord(p);
    if (!row) continue;
    const barTime = asTime(row.bar_time) ?? asTime(row.time) ?? asTime(row.timestamp);
    if (!barTime) continue;
    const barIndex = finiteNum(row.bar_index);
    let high = finiteNum(row.high) ?? finiteNum(row.pattern_high);
    let low = finiteNum(row.low) ?? finiteNum(row.pattern_low);
    if (high != null && low != null && high < low) {
      const tmp = high;
      high = low;
      low = tmp;
    }
    out.push({
      barTime,
      barIndex: barIndex != null ? Math.round(barIndex) : null,
      high,
      low,
      mid: finiteNum(row.mid) ?? (high != null && low != null ? (high + low) / 2 : null),
    });
  }
  return out;
}

function parseChartDrawKind(raw: unknown): string | null {
  const row = asRecord(raw);
  if (!row) return null;
  const kind = typeof row.kind === "string" ? row.kind.trim().toLowerCase() : "";
  return kind || null;
}

function parseChartDrawOscSeries(raw: unknown): string | null {
  const row = asRecord(raw);
  if (!row) return null;
  const series =
    typeof row.osc_series === "string" ? row.osc_series.trim().toLowerCase() : "";
  if (
    series === "histogram" ||
    series === "macd_line" ||
    series === "rsi" ||
    series === "stoch_k"
  ) {
    return series;
  }
  return null;
}

function parseChartDrawSignalBarPills(raw: unknown): string[] {
  const row = asRecord(raw);
  if (!row || !Array.isArray(row.signal_bar_pills)) return [];
  const out: string[] = [];
  for (const p of row.signal_bar_pills) {
    if (typeof p !== "string") continue;
    const t = p.trim().toLowerCase();
    if (t) out.push(t);
  }
  return out;
}

/** Resolve which oscillator series divergence slopes belong on. */
export function strategyRdResolveOscSeries(input: {
  chartDrawOscSeries?: string | null;
  oscillator?: string | null;
  source?: string | null;
  conceptId?: string | null;
}): "histogram" | "macd_line" | "rsi" | "stoch_k" {
  const fromDraw = (input.chartDrawOscSeries || "").trim().toLowerCase();
  if (
    fromDraw === "histogram" ||
    fromDraw === "macd_line" ||
    fromDraw === "rsi" ||
    fromDraw === "stoch_k"
  ) {
    return fromDraw;
  }
  const osc = (input.oscillator || "").trim().toLowerCase();
  const source = (input.source || "").trim().toLowerCase();
  const id = (input.conceptId || "").trim().toLowerCase();
  if (osc === "macd" || id.includes("macd")) {
    return source === "hist" || source === "histogram" ? "histogram" : "macd_line";
  }
  if (osc === "stoch" || id.includes("stoch")) return "stoch_k";
  return "rsi";
}

export function strategyRdOscSeriesPaneId(
  series: "histogram" | "macd_line" | "rsi" | "stoch_k"
): "tessera-macd" | "tessera-panes" {
  return series === "histogram" || series === "macd_line"
    ? "tessera-macd"
    : "tessera-panes";
}

function emptyLocation(): StrategyRdWaveCLocation {
  return {
    barTime: null,
    barIndex: null,
    direction: null,
    candleType: null,
    patternStart: null,
    patternEnd: null,
    patternStartTime: null,
    patternEndTime: null,
    patternHigh: null,
    patternLow: null,
    levelHigh: null,
    levelLow: null,
    neckline: null,
    necklineSlope: null,
    necklineLeft: null,
    necklineRight: null,
    necklineLeftBar: null,
    necklineRightBar: null,
    leftShoulder: null,
    head: null,
    rightShoulder: null,
    leftShoulderTime: null,
    headTime: null,
    rightShoulderTime: null,
    pivots: [],
    upperLine: null,
    lowerLine: null,
    pole: null,
    channelKind: null,
    triangleKind: null,
    converging: null,
    widthStart: null,
    widthEnd: null,
    breakLevel: null,
    upperBound: null,
    lowerBound: null,
    upperSlope: null,
    lowerSlope: null,
    spanStart: null,
    spanEnd: null,
    spanStartTime: null,
    spanEndTime: null,
    channelStartTime: null,
    channelEndTime: null,
    donchianPath: [],
    chartDrawKind: null,
    chartDrawOscSeries: null,
    chartDrawSignalBarPills: [],
    oscSource: null,
    oscFast: null,
    oscSlow: null,
    oscSignal: null,
    oscPeriod: null,
    oscLength: null,
    oscSmoothK: null,
    oscSmoothD: null,
    upper: null,
    mid: null,
    lower: null,
    zone: null,
    positionRatio: null,
    donchianHigh: null,
    donchianLow: null,
    maValue: null,
    cupLeft: null,
    cupRight: null,
    handleBar: null,
    cupLeftTime: null,
    cupRightTime: null,
    cupBottomTime: null,
    handleBarTime: null,
    handlePrice: null,
    rim: null,
    cupLow: null,
    cupHigh: null,
    cupPath: [],
    poleStart: null,
    poleEnd: null,
    flagStart: null,
    flagEnd: null,
    poleStartTime: null,
    poleEndTime: null,
    flagStartTime: null,
    flagEndTime: null,
    sessionId: null,
    hour: null,
    rsi: null,
    macd: null,
    macdSignal: null,
    macdHist: null,
    macdHistPrev: null,
    stochK: null,
    stochD: null,
    mfi: null,
    adx: null,
    atrPct: null,
    atrRank: null,
    bucket: null,
    rs: null,
    mansfieldRs: null,
    corr: null,
    paceMultiple: null,
    upDownVolRatio: null,
    volume: null,
    volumePrior: null,
    volExpandRatio: null,
    volExpandFactor: null,
    volExpanded: null,
    rvol: null,
    rvolAvg: null,
    rvolLookback: null,
    volumeAvailable: null,
    softPass: null,
    divPivot1Time: null,
    divPivot2Time: null,
    divPivot1Index: null,
    divPivot2Index: null,
    divPrice1: null,
    divPrice2: null,
    divOsc1: null,
    divOsc2: null,
    divClass: null,
    divSide: null,
    divPriceLine: null,
    divOscLine: null,
    divReclaimLevel: null,
    divStructureBreakLevel: null,
    divStructureBreakBarTime: null,
    divStructureBreakBarIndex: null,
    divStructureBreakPivotTime: null,
    divStructureBreakPivotIndex: null,
    divLabel: null,
  };
}

/** Normalize drawable Wave C fields from a setup meta or confluence evidence row. */
export function parseStrategyRdWaveCLocation(
  raw: Record<string, unknown> | null | undefined
): StrategyRdWaveCLocation {
  if (!raw) return emptyLocation();
  const barIndex = finiteNum(raw.bar_index);
  return {
    barTime: asTime(raw.bar_time) ?? asTime(raw.timestamp) ?? asTime(raw.time),
    barIndex: barIndex != null ? Math.round(barIndex) : null,
    direction: asDirection(raw.direction),
    candleType:
      typeof raw.candle_type === "string" && raw.candle_type.trim()
        ? raw.candle_type.trim()
        : null,
    patternStart: finiteNum(raw.pattern_start),
    patternEnd: finiteNum(raw.pattern_end),
    patternStartTime: asTime(raw.pattern_start_time),
    patternEndTime: asTime(raw.pattern_end_time),
    patternHigh: finiteNum(raw.pattern_high),
    patternLow: finiteNum(raw.pattern_low),
    levelHigh: finiteNum(raw.level_high),
    levelLow: finiteNum(raw.level_low),
    neckline: finiteNum(raw.neckline),
    necklineSlope: finiteNum(raw.neckline_slope),
    necklineLeft: finiteNum(raw.neckline_left),
    necklineRight: finiteNum(raw.neckline_right),
    necklineLeftBar: (() => {
      const n = finiteNum(raw.neckline_left_bar);
      return n != null ? Math.round(n) : null;
    })(),
    necklineRightBar: (() => {
      const n = finiteNum(raw.neckline_right_bar);
      return n != null ? Math.round(n) : null;
    })(),
    leftShoulder: finiteNum(raw.left_shoulder),
    head: finiteNum(raw.head),
    rightShoulder: finiteNum(raw.right_shoulder),
    leftShoulderTime: asTime(raw.left_shoulder_time),
    headTime: asTime(raw.head_time),
    rightShoulderTime: asTime(raw.right_shoulder_time),
    pivots: parsePivots(raw.pivots),
    upperLine: parseTrendLine(raw.upper_line),
    lowerLine: parseTrendLine(raw.lower_line),
    pole: parseTrendLine(raw.pole),
    channelKind: (() => {
      const k =
        typeof raw.channel_kind === "string" ? raw.channel_kind.trim().toLowerCase() : "";
      if (k === "flag" || k === "pennant" || k === "triangle" || k === "rectangle") return k;
      return null;
    })(),
    triangleKind: (() => {
      const k =
        typeof raw.triangle_kind === "string" ? raw.triangle_kind.trim().toLowerCase() : "";
      if (k === "asc" || k === "ascending") return "asc";
      if (k === "desc" || k === "descending") return "desc";
      if (k === "sym" || k === "symmetrical" || k === "symmetric") return "sym";
      return null;
    })(),
    converging: typeof raw.converging === "boolean" ? raw.converging : null,
    widthStart: finiteNum(raw.width_start),
    widthEnd: finiteNum(raw.width_end),
    breakLevel: finiteNum(raw.break_level),
    upperBound: finiteNum(raw.upper_bound),
    lowerBound: finiteNum(raw.lower_bound),
    upperSlope: finiteNum(raw.upper_slope),
    lowerSlope: finiteNum(raw.lower_slope),
    spanStart: finiteNum(raw.span_start),
    spanEnd: finiteNum(raw.span_end),
    spanStartTime: asTime(raw.span_start_time),
    spanEndTime: asTime(raw.span_end_time),
    channelStartTime:
      asTime(raw.channel_start_time) ??
      asTime(raw.channel_start) ??
      asTime(raw.from_bar_time),
    channelEndTime:
      asTime(raw.channel_end_time) ??
      asTime(raw.channel_end) ??
      asTime(raw.to_bar_time),
    donchianPath: parseDonchianPath(raw.donchian_path),
    chartDrawKind: parseChartDrawKind(raw.chart_draw),
    chartDrawOscSeries: parseChartDrawOscSeries(raw.chart_draw),
    chartDrawSignalBarPills: parseChartDrawSignalBarPills(raw.chart_draw),
    oscSource:
      typeof raw.source === "string" ? raw.source.trim().toLowerCase() || null : null,
    oscFast: finiteNum(raw.fast),
    oscSlow: finiteNum(raw.slow),
    oscSignal: finiteNum(raw.signal),
    oscPeriod: finiteNum(raw.period) ?? finiteNum(raw.length),
    oscLength: finiteNum(raw.length) ?? finiteNum(raw.period),
    oscSmoothK: finiteNum(raw.smooth_k) ?? finiteNum(raw.smooth),
    oscSmoothD: finiteNum(raw.smooth_d) ?? finiteNum(raw.smooth),
    upper: finiteNum(raw.upper) ?? finiteNum(raw.bb_upper),
    mid: finiteNum(raw.mid) ?? finiteNum(raw.bb_middle) ?? finiteNum(raw.bb_mid),
    lower: finiteNum(raw.lower) ?? finiteNum(raw.bb_lower),
    zone: typeof raw.zone === "string" ? raw.zone.trim() || null : null,
    positionRatio: finiteNum(raw.position_ratio),
    donchianHigh: finiteNum(raw.donchian_high) ?? finiteNum(raw.donch_high),
    donchianLow: finiteNum(raw.donchian_low) ?? finiteNum(raw.donch_low),
    maValue: finiteNum(raw.ma_value) ?? finiteNum(raw.value),
    cupLeft: finiteNum(raw.cup_left),
    cupRight: finiteNum(raw.cup_right),
    handleBar: finiteNum(raw.handle_bar),
    cupLeftTime: asTime(raw.cup_left_time),
    cupRightTime: asTime(raw.cup_right_time),
    cupBottomTime: asTime(raw.cup_bottom_time),
    handleBarTime: asTime(raw.handle_time) ?? asTime(raw.handle_bar_time),
    handlePrice: finiteNum(raw.handle) ?? finiteNum(raw.handle_price),
    rim: finiteNum(raw.rim),
    cupLow: finiteNum(raw.cup_low),
    cupHigh: finiteNum(raw.cup_high),
    cupPath: parsePivots(raw.cup_path),
    poleStart: finiteNum(raw.pole_start),
    poleEnd: finiteNum(raw.pole_end),
    flagStart: finiteNum(raw.flag_start),
    flagEnd: finiteNum(raw.flag_end),
    poleStartTime: asTime(raw.pole_start_time),
    poleEndTime: asTime(raw.pole_end_time),
    flagStartTime: asTime(raw.flag_start_time),
    flagEndTime: asTime(raw.flag_end_time),
    sessionId:
      typeof raw.session_id === "string" ? raw.session_id.trim().toLowerCase() || null : null,
    hour: finiteNum(raw.hour),
    rsi: finiteNum(raw.rsi),
    macd: finiteNum(raw.macd) ?? finiteNum(raw.macd_line),
    macdSignal: finiteNum(raw.macd_signal),
    macdHist: finiteNum(raw.macd_hist),
    macdHistPrev: finiteNum(raw.macd_hist_prev),
    stochK: finiteNum(raw.stoch_k),
    stochD: finiteNum(raw.stoch_d),
    mfi: finiteNum(raw.mfi),
    adx: finiteNum(raw.adx),
    atrPct: finiteNum(raw.atr_pct) ?? finiteNum(raw.atr),
    atrRank: finiteNum(raw.atr_rank),
    bucket: typeof raw.bucket === "string" ? raw.bucket.trim() || null : null,
    rs: finiteNum(raw.rs),
    mansfieldRs: finiteNum(raw.mansfield_rs),
    corr: finiteNum(raw.corr),
    paceMultiple: finiteNum(raw.pace_multiple),
    upDownVolRatio: finiteNum(raw.up_down_vol_ratio),
    volume: finiteNum(raw.volume),
    volumePrior: finiteNum(raw.volume_prior),
    volExpandRatio: finiteNum(raw.vol_expand_ratio),
    volExpandFactor: finiteNum(raw.vol_expand_factor),
    volExpanded:
      typeof raw.vol_expanded === "boolean"
        ? raw.vol_expanded
        : raw.vol_expanded === "true"
          ? true
          : raw.vol_expanded === "false"
            ? false
            : null,
    rvol: finiteNum(raw.rvol),
    rvolAvg: finiteNum(raw.rvol_avg),
    rvolLookback: finiteNum(raw.rvol_lookback),
    volumeAvailable:
      typeof raw.volume_available === "boolean"
        ? raw.volume_available
        : (() => {
            const status = asRecord(raw.volume_status);
            return typeof status?.available === "boolean" ? status.available : null;
          })(),
    softPass: typeof raw.soft_pass === "string" ? raw.soft_pass.trim() || null : null,
    // Prefer explicit *_time; pivot1/pivot2 are bar indexes (not ISO strings).
    divPivot1Time: asTime(raw.pivot1_time),
    divPivot2Time: asTime(raw.pivot2_time),
    divPivot1Index: (() => {
      const n = finiteNum(raw.pivot1);
      return n != null ? Math.round(n) : null;
    })(),
    divPivot2Index: (() => {
      const n = finiteNum(raw.pivot2);
      return n != null ? Math.round(n) : null;
    })(),
    divPrice1: finiteNum(raw.price1),
    divPrice2: finiteNum(raw.price2),
    divOsc1: finiteNum(raw.osc1) ?? finiteNum(raw.rsi1),
    divOsc2: finiteNum(raw.osc2) ?? finiteNum(raw.rsi2),
    divClass: typeof raw.div_class === "string" ? raw.div_class.trim() || null : null,
    divSide:
      typeof raw.side === "string"
        ? raw.side.trim().toLowerCase() || null
        : null,
    divPriceLine: parseTrendLine(raw.price_line),
    divOscLine: parseTrendLine(raw.osc_line),
    divReclaimLevel: finiteNum(raw.reclaim_level),
    divStructureBreakLevel: finiteNum(raw.structure_break_level),
    divStructureBreakBarTime:
      asTime(raw.structure_break_bar_time) ??
      asTime(raw.structure_break_time),
    divStructureBreakBarIndex: (() => {
      const n =
        finiteNum(raw.structure_break_bar_index) ??
        finiteNum(raw.structure_break_bar);
      return n != null ? Math.round(n) : null;
    })(),
    divStructureBreakPivotTime: asTime(raw.structure_break_pivot_time),
    divStructureBreakPivotIndex: (() => {
      const n = finiteNum(raw.structure_break_pivot_index);
      return n != null ? Math.round(n) : null;
    })(),
    divLabel:
      typeof raw.div_label === "string"
        ? raw.div_label.trim() || null
        : typeof raw.label === "string" &&
            String(raw.label).toLowerCase().includes("div")
          ? raw.label.trim() || null
          : null,
  };
}

export function strategyRdWaveCHasCandleSpan(loc: StrategyRdWaveCLocation): boolean {
  return Boolean(
    (loc.spanStartTime && loc.spanEndTime) ||
      (loc.spanStart != null && loc.spanEnd != null) ||
      (loc.patternHigh != null && loc.patternLow != null && loc.barTime)
  );
}

export function strategyRdWaveCHasNecklinePattern(loc: StrategyRdWaveCLocation): boolean {
  return loc.pivots.length >= 2 && loc.neckline != null;
}

/**
 * Sloped neckline segment for H&S (BE `neckline_left`/`right` + pivot times).
 * Falls back to pivots[1]→pivots[3] when BE sends the standard 5-pivot pack.
 * Endpoint at the signal bar uses `neckline` (price of the trendline at break).
 */
export function strategyRdWaveCNecklineSegment(loc: StrategyRdWaveCLocation): {
  fromBarTime: string;
  toBarTime: string;
  fromPrice: number;
  toPrice: number;
} | null {
  const pivotAtBar = (bar: number | null): StrategyRdWaveCPivot | null => {
    if (bar == null) return null;
    return loc.pivots.find((p) => p.barIndex === bar) ?? null;
  };
  let left = pivotAtBar(loc.necklineLeftBar);
  let right = pivotAtBar(loc.necklineRightBar);
  if (
    (!left || !right) &&
    loc.pivots.length >= 5 &&
    (loc.leftShoulder != null || loc.head != null)
  ) {
    // Classic pack: LS, neck-left, Head, neck-right, RS.
    left = left ?? loc.pivots[1] ?? null;
    right = right ?? loc.pivots[3] ?? null;
  }
  const fromPrice = loc.necklineLeft ?? left?.price ?? null;
  const midPrice = loc.necklineRight ?? right?.price ?? null;
  const fromBarTime = left?.barTime ?? null;
  const rightTime = right?.barTime ?? null;
  if (fromPrice == null || midPrice == null || !fromBarTime || !rightTime) return null;
  if (fromBarTime === rightTime && fromPrice === midPrice) return null;

  const toBarTime = loc.barTime && loc.barTime !== fromBarTime ? loc.barTime : rightTime;
  const toPrice =
    toBarTime === rightTime || loc.neckline == null ? midPrice : loc.neckline;
  if (toBarTime === fromBarTime) return null;
  return { fromBarTime, toBarTime, fromPrice, toPrice };
}

export function strategyRdWaveCHasChannel(loc: StrategyRdWaveCLocation): boolean {
  return (
    loc.upperBound != null &&
    loc.lowerBound != null &&
    (loc.upperSlope != null || loc.lowerSlope != null || loc.patternStartTime != null)
  );
}

export function strategyRdWaveCIsWedgeId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return key === "wedge" || key.includes("wedge");
}

export function strategyRdWaveCHasWedgeLines(loc: StrategyRdWaveCLocation): boolean {
  return Boolean(loc.upperLine && loc.lowerLine);
}

export function strategyRdWaveCIsFlagId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return key === "flag" || key.includes("flag");
}

export function strategyRdWaveCIsPennantId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return key === "pennant" || key.includes("pennant");
}

/** Pole mast and/or channel lines (BE flag / pennant meta). */
export function strategyRdWaveCHasFlagGeometry(loc: StrategyRdWaveCLocation): boolean {
  return Boolean(loc.pole || (loc.upperLine && loc.lowerLine));
}

/** Prefer concept id; fall back to BE `channel_kind`. */
export function strategyRdWaveCIsPennantMeta(
  conceptId: string,
  loc: StrategyRdWaveCLocation
): boolean {
  return strategyRdWaveCIsPennantId(conceptId) || loc.channelKind === "pennant";
}

export function strategyRdWaveCIsTriangleId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return key === "triangle" || key.includes("triangle");
}

export function strategyRdWaveCIsRectangleId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return key === "rectangle" || key.includes("rectangle");
}

/** Triangle setup (concept id or BE `channel_kind`). */
export function strategyRdWaveCIsTriangleMeta(
  conceptId: string,
  loc: StrategyRdWaveCLocation
): boolean {
  return strategyRdWaveCIsTriangleId(conceptId) || loc.channelKind === "triangle";
}

/** Rectangle setup (concept id or BE `channel_kind`). */
export function strategyRdWaveCIsRectangleMeta(
  conceptId: string,
  loc: StrategyRdWaveCLocation
): boolean {
  return strategyRdWaveCIsRectangleId(conceptId) || loc.channelKind === "rectangle";
}

/**
 * Chart label for triangle / rectangle — Investopedia types (asc / desc / sym).
 * Prefer BE `triangle_kind`, then concept id suffix.
 */
export function strategyRdWaveCTrianglePatternLabel(
  conceptId: string,
  loc: StrategyRdWaveCLocation
): string {
  if (strategyRdWaveCIsRectangleMeta(conceptId, loc)) return "Rectangle";
  const kind =
    loc.triangleKind ??
    (() => {
      const key = conceptId.trim().toLowerCase();
      if (key.includes("asc")) return "asc" as const;
      if (key.includes("desc")) return "desc" as const;
      if (key.includes("sym")) return "sym" as const;
      return null;
    })();
  if (kind === "asc") return "Ascending";
  if (kind === "desc") return "Descending";
  if (kind === "sym") return "Symmetrical";
  return "Triangle";
}

export function strategyRdWaveCIsCupId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return key.includes("cup_handle") || key.includes("cup_and_handle") || key === "cup";
}

export function strategyRdWaveCHasCup(loc: StrategyRdWaveCLocation): boolean {
  if (loc.cupPath.length >= 2 && loc.rim != null) return true;
  return Boolean(
    (loc.cupLeftTime || loc.cupLeft != null) &&
      (loc.cupRightTime || loc.cupRight != null) &&
      loc.rim != null
  );
}

export function strategyRdWaveCHasBands(loc: StrategyRdWaveCLocation): boolean {
  return (
    (loc.upper != null && loc.lower != null) ||
    (loc.donchianHigh != null && loc.donchianLow != null) ||
    loc.maValue != null
  );
}

export function strategyRdWaveCIsOscillatorId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return (
    /(^|_)rsi($|_)/.test(key) ||
    /(^|_)macd($|_)/.test(key) ||
    /(^|_)stoch($|_)/.test(key) ||
    /(^|_)mfi($|_)/.test(key) ||
    /(^|_)adx($|_)/.test(key) ||
    /(^|_)atr($|_)/.test(key) ||
    key.includes("atr_regime")
  );
}

export function strategyRdWaveCIsRsId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return (
    key === "rs_vs_benchmark" ||
    key === "mansfield_rs" ||
    key === "corr_rs" ||
    key === "own_momentum" ||
    key.includes("mansfield") ||
    key.includes("corr_rs")
  );
}

export function strategyRdWaveCIsVolumeId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return (
    key.includes("vol_") ||
    key.includes("volume") ||
    key === "rvol_strong" ||
    key === "vol_expansion" ||
    key === "vol_dryup" ||
    key === "vol_vs_eod_pace" ||
    key === "up_down_vol_ratio"
  );
}

/** True when BE sent drawable volume numbers (not merely volume_available: false). */
export function strategyRdWaveCHasVolumeEvidence(
  loc: Pick<
    StrategyRdWaveCLocation,
    | "volume"
    | "volumePrior"
    | "volExpandRatio"
    | "volExpandFactor"
    | "volExpanded"
    | "rvol"
    | "rvolAvg"
    | "paceMultiple"
    | "upDownVolRatio"
    | "volumeAvailable"
  >
): boolean {
  if (loc.volumeAvailable === false) return false;
  return (
    loc.volume != null ||
    loc.volumePrior != null ||
    loc.volExpandRatio != null ||
    loc.volExpandFactor != null ||
    loc.volExpanded === true ||
    loc.rvol != null ||
    loc.rvolAvg != null ||
    loc.paceMultiple != null ||
    loc.upDownVolRatio != null
  );
}

/**
 * Volume-pane label from BE numbers — prefer RVOL / expand ratio over bare concept name.
 */
export function strategyRdWaveCVolumeLabel(
  loc: Pick<
    StrategyRdWaveCLocation,
    | "rvol"
    | "volExpandRatio"
    | "volExpandFactor"
    | "volExpanded"
    | "paceMultiple"
    | "upDownVolRatio"
    | "volume"
    | "volumePrior"
  >,
  conceptLabel: string,
  /** Raw evidence — the §1.6d volume keys (thresholds, pace, up / down volumes). */
  raw?: Record<string, unknown> | null
): string {
  const base = conceptLabel.trim() || "Volume";
  const n = (v: unknown): number | null => {
    if (v == null || v === "") return null;
    const x = typeof v === "number" ? v : Number(v);
    return Number.isFinite(x) ? x : null;
  };
  if (raw) {
    const pace = n(raw.pace_multiple);
    // §1.6d pace evidence (older dry-up / expansion rows also carried pace_multiple).
    if (pace != null && (n(raw.volume_usual_by_now) != null || n(raw.projected_day_volume) != null)) {
      const projected = n(raw.projected_day_volume);
      return `Pace ${pace.toFixed(1)}\u00d7 usual day${projected != null ? ` \u00b7 proj ${formatVolumeShort(projected)}` : ""}`;
    }
    const up = n(raw.up_volume);
    const down = n(raw.down_volume);
    const ratio = n(raw.up_down_vol_ratio) ?? loc.upDownVolRatio;
    if (ratio != null && (up != null || down != null)) {
      return `Up/down ${ratio.toFixed(2)} (${formatVolumeShort(up ?? 0)} up / ${formatVolumeShort(down ?? 0)} down)`;
    }
    const minRvol = n(raw.min_rvol);
    const maxRvol = n(raw.max_rvol);
    if (loc.rvol != null && (minRvol != null || maxRvol != null)) {
      const limit = maxRvol != null ? `<${maxRvol.toFixed(1)}\u00d7` : `\u2265${minRvol!.toFixed(1)}\u00d7`;
      return `RVol ${loc.rvol.toFixed(1)}\u00d7 normal (${limit})`;
    }
    const expRatio = n(raw.vol_expand_ratio);
    if (expRatio != null && n(raw.volume_prior) != null) {
      const factor = n(raw.vol_expand_factor);
      return `Vol+ ${expRatio.toFixed(1)}\u00d7 prev bar${factor != null ? ` (\u2265${factor.toFixed(1)}\u00d7)` : ""}`;
    }
  }
  if (loc.rvol != null) {
    const compact = /rvol/i.test(base) ? base : `${base} RVOL`;
    return `${compact} ${loc.rvol.toFixed(1)}×`;
  }
  const expand = loc.volExpandRatio ?? loc.volExpandFactor ?? loc.paceMultiple;
  if (expand != null) {
    const tag =
      loc.volExpanded === false || /vol\s*-|dry/i.test(base) ? "Vol-" : "Vol+";
    const head = /^vol[+\-]?$/i.test(base) || /vol\+|vol-/i.test(base) ? base : `${base} ${tag}`;
    return `${head} ${expand.toFixed(1)}×`;
  }
  if (loc.upDownVolRatio != null) {
    return `${base} ${loc.upDownVolRatio.toFixed(2)}`;
  }
  if (loc.volume != null && loc.volumePrior != null && loc.volumePrior > 0) {
    return `${base} ${(loc.volume / loc.volumePrior).toFixed(1)}×`;
  }
  if (loc.volume != null) {
    return `${base} ${formatVolumeShort(loc.volume)}`;
  }
  return base;
}

/** Y on the volume pane — prefer bar volume, else RVOL / expand multiple. */
export function strategyRdWaveCVolumePanePrice(
  loc: Pick<
    StrategyRdWaveCLocation,
    "volume" | "rvol" | "volExpandRatio" | "volExpandFactor" | "paceMultiple"
  >
): number | null {
  if (loc.volume != null && loc.volume >= 0) return loc.volume;
  if (loc.rvol != null) return loc.rvol;
  return loc.volExpandRatio ?? loc.volExpandFactor ?? loc.paceMultiple ?? null;
}

function formatVolumeShort(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return Number.isInteger(n) ? String(n) : n.toFixed(0);
}

export function strategyRdWaveCIsKillzoneId(id: string): boolean {
  return id.trim().toLowerCase() === "killzone" || id.trim().toLowerCase().includes("killzone");
}

export function strategyRdWaveCIsChartPatternId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return (
    key.includes("head_shoulder") ||
    key.includes("double_") ||
    key.includes("triple_") ||
    key.includes("wedge") ||
    key.includes("triangle") ||
    key.includes("rectangle") ||
    key.includes("flag") ||
    key.includes("pennant") ||
    key.includes("cup_handle") ||
    key.includes("cup_and_handle")
  );
}

export function strategyRdWaveCIsCandlePatternId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return (
    key.includes("doji") ||
    key.includes("hammer") ||
    key.includes("hanging_man") ||
    key.includes("shooting_star") ||
    key.includes("marubozu") ||
    key.includes("harami") ||
    key.includes("tweezer") ||
    key.includes("piercing") ||
    key.includes("dark_cloud") ||
    key.includes("cloud_pierce") ||
    key.includes("morning_star") ||
    key.includes("evening_star") ||
    key.includes("star_triple") ||
    key.includes("three_inside") ||
    key.includes("three_white") ||
    key.includes("three_black") ||
    key.includes("three_soldiers") ||
    key.includes("three_crows") ||
    key.includes("engulf") ||
    key.includes("inside_bar")
  );
}

export function strategyRdWaveCIsDonchianId(id: string): boolean {
  return id.trim().toLowerCase().includes("donchian");
}

export function strategyRdWaveCIsDivergenceId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return (
    key.includes("rsi_div") ||
    key.includes("macd_div") ||
    key.includes("stoch_div") ||
    key.includes("divergence") ||
    /(^|_)div($|_)/.test(key)
  );
}

export function strategyRdWaveCIsMacdCrossZeroId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return (
    key.includes("macd_cross") ||
    key.includes("macd_zero") ||
    key === "macd_zero_cross"
  );
}

export function strategyRdWaveCIsMacdCrossZeroChartDraw(
  loc: Pick<StrategyRdWaveCLocation, "chartDrawKind">
): boolean {
  const kind = (loc.chartDrawKind || "").trim().toLowerCase();
  return kind === "macd_cross" || kind === "macd_zero";
}

export function strategyRdWaveCIsDivergenceChartDraw(
  loc: Pick<StrategyRdWaveCLocation, "chartDrawKind">
): boolean {
  return (loc.chartDrawKind || "").endsWith("_divergence");
}

/** Resolve divergence swing bar times (prefer BE times; else candle index). */
export function strategyRdWaveCResolveDivPivotTimes(
  loc: Pick<
    StrategyRdWaveCLocation,
    | "divPivot1Time"
    | "divPivot2Time"
    | "divPivot1Index"
    | "divPivot2Index"
    | "divPriceLine"
  >,
  candles?: readonly { timestamp?: string | null; time?: string | null }[] | null
): { t1: string; t2: string } | null {
  if (loc.divPriceLine) {
    return { t1: loc.divPriceLine.p1.barTime, t2: loc.divPriceLine.p2.barTime };
  }
  const timeAt = (index: number | null): string | null => {
    if (index == null || !candles?.length) return null;
    if (index < 0 || index >= candles.length) return null;
    const c = candles[index];
    const t = (c?.timestamp || c?.time || "").trim();
    return t || null;
  };
  const t1 = loc.divPivot1Time ?? timeAt(loc.divPivot1Index);
  const t2 = loc.divPivot2Time ?? timeAt(loc.divPivot2Index);
  if (!t1 || !t2 || t1 === t2) return null;
  return { t1, t2 };
}

export function strategyRdWaveCHasDivergenceSwings(
  loc: Pick<
    StrategyRdWaveCLocation,
    | "divPivot1Time"
    | "divPivot2Time"
    | "divPivot1Index"
    | "divPivot2Index"
    | "divPrice1"
    | "divPrice2"
    | "divPriceLine"
    | "divOscLine"
  >,
  candles?: readonly { timestamp?: string | null; time?: string | null }[] | null
): boolean {
  if (loc.divPriceLine) return true;
  if (loc.divOscLine && (loc.divPrice1 != null || loc.divPrice2 != null)) return true;
  if (loc.divPrice1 == null || loc.divPrice2 == null) return false;
  return strategyRdWaveCResolveDivPivotTimes(loc, candles) != null;
}

export function strategyRdWaveCIsDonchianChartDraw(
  loc: Pick<StrategyRdWaveCLocation, "chartDrawKind">
): boolean {
  return (loc.chartDrawKind || "").includes("donchian");
}

/** True when BE sent drawable Donchian channel rails (not a one-bar level box). */
export function strategyRdWaveCHasDonchianChannel(
  loc: Pick<
    StrategyRdWaveCLocation,
    | "upperLine"
    | "lowerLine"
    | "donchianHigh"
    | "donchianLow"
    | "channelStartTime"
    | "channelEndTime"
    | "donchianPath"
  >
): boolean {
  if (loc.upperLine && loc.lowerLine) return true;
  if (
    loc.donchianHigh != null &&
    loc.donchianLow != null &&
    loc.channelStartTime &&
    loc.channelEndTime
  ) {
    return true;
  }
  return loc.donchianPath.length >= 2;
}

export function strategyRdWaveCIsBandId(id: string): boolean {
  const key = id.trim().toLowerCase();
  return (
    key.includes("bb_zone") ||
    key.includes("bb_close") ||
    key.includes("bb_squeeze") ||
    key.includes("donchian") ||
    key.includes("ttm_squeeze") ||
    key === "sma_cross" ||
    key === "ema_cross"
  );
}

/** UTC session shade window for the UTC day of `barTime`. */
export function strategyRdKillzoneWindowUtc(opts: {
  barTime: string;
  startHour: number;
  endHour: number;
}): { fromIso: string; toIso: string } | null {
  const ms = Date.parse(opts.barTime.replace(/\+00:00$/, "Z"));
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const startH = ((Math.floor(opts.startHour) % 24) + 24) % 24;
  const endH = ((Math.floor(opts.endHour) % 24) + 24) % 24;
  let from = Date.UTC(y, m, day, startH, 0, 0);
  let to = Date.UTC(y, m, day, endH, 0, 0);
  if (to <= from) to += 24 * 60 * 60 * 1000;
  return { fromIso: new Date(from).toISOString(), toIso: new Date(to).toISOString() };
}

/** Price at barIndex using bound + slope*(i - startIndex). */
export function strategyRdChannelPriceAt(
  bound: number,
  slope: number | null,
  barIndex: number,
  startIndex: number
): number {
  if (slope == null || !Number.isFinite(slope)) return bound;
  return bound + slope * (barIndex - startIndex);
}
